import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Container,
  CardContent,
  Grid,
  Stack,
  Avatar,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  People,
  Assessment,
  Settings,
  TrendingUp,
  PersonAdd,
  MoreVert,
  Business,
  Analytics,
  Euro,
  CheckCircle,
  Schedule,
  Group,
  TrendingDown,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import Card from "../components/Card";
import api from "../api";

interface DashboardData {
  overview: {
    totalClients: number;
    newClientsThisMonth: number;
    activeClients: number;
    pendingClients: number;
    totalPartners: number;
    activePartners: number;
    potentialRevenue: number;
    partnerRevenue: number;
    clientGrowthPercentage: number;
    systemUptime: number;
  };
  distribution: {
    statusDistribution: { _id: string; count: number }[];
    typeDistribution: { _id: string; count: number }[];
    topCities: { _id: string; count: number }[];
  };
  trends: {
    monthlyClientTrend: { _id: { year: number; month: number }; count: number }[];
  };
  recentActivity: {
    clients: {
      _id: string;
      name: string;
      trackingCode: string;
      status: string;
      createdAt: string;
      type: string;
    }[];
    partners: {
      _id: string;
      name: string;
      partnerCode: string;
      createdAt: string;
    }[];
  };
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/dashboard');
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value}%`;
  };

  const getGrowthColor = (value: number) => {
    return value >= 0 ? 'success.main' : 'error.main';
  };

  const getGrowthIcon = (value: number) => {
    return value >= 0 ? <TrendingUp /> : <TrendingDown />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error" action={
          <Button onClick={fetchDashboardData}>Tentar novamente</Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!data) return null;

  const quickActions = [
    {
      title: "Lista de Clientes",
      description: "Visualize e gerencie todos os registros de clientes",
      icon: <People />,
      color: "primary",
      path: "/clients",
      stats: `${data.overview.totalClients} clientes`,
    },
    {
      title: "Gestão de Parceiros",
      description: "Gerencie parceiros e suas indicações",
      icon: <Business />,
      color: "secondary",
      path: "/partners",
      stats: `${data.overview.totalPartners} parceiros`,
    },
    {
      title: "Dashboard Financeiro",
      description: "Monitore pagamentos e receitas potenciais",
      icon: <Analytics />,
      color: "success",
      path: "/financial-dashboard",
      stats: formatCurrency(data.overview.potentialRevenue),
    },
    {
      title: "Configurações",
      description: "Configure taxas e preferências do sistema",
      icon: <Settings />,
      color: "warning",
      path: "/settings",
      stats: "Sistema",
    },
  ];

  const recentActivity = [
    ...data.recentActivity.clients.map(client => ({
      action: `Novo cliente registrado: ${client.name}`,
      time: new Date(client.createdAt).toLocaleDateString('pt-BR'),
      user: client.trackingCode,
      type: 'client'
    })),
    ...data.recentActivity.partners.map(partner => ({
      action: `Novo parceiro cadastrado: ${partner.name}`,
      time: new Date(partner.createdAt).toLocaleDateString('pt-BR'),
      user: partner.partnerCode,
      type: 'partner'
    }))
  ].slice(0, 5);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: (theme) => theme.palette.grey[100],
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={4}>
          {/* Header */}
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
              Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Bem-vindo de volta, {user?.username}! Aqui está um resumo do seu
              sistema.
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, mb: 0.5 }}
                      >
                        {data.overview.totalClients}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Clientes
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: "primary.main",
                        background: (theme) =>
                          `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      }}
                    >
                      <People />
                    </Avatar>
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 2 }}
                  >
                    {getGrowthIcon(data.overview.clientGrowthPercentage)}
                    <Typography variant="caption" sx={{ color: getGrowthColor(data.overview.clientGrowthPercentage) }}>
                      {formatPercentage(data.overview.clientGrowthPercentage)} vs mês passado
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, mb: 0.5 }}
                      >
                        {data.overview.newClientsThisMonth}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Novos este mês
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: "info.main",
                        background: (theme) =>
                          `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                      }}
                    >
                      <PersonAdd />
                    </Avatar>
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 2 }}
                  >
                    <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
                    <Typography variant="caption" color="success.main">
                      Em crescimento
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, mb: 0.5 }}
                      >
                        {data.overview.activeClients}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Clientes Ativos
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: "success.main",
                        background: (theme) =>
                          `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                      }}
                    >
                      <CheckCircle />
                    </Avatar>
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 2 }}
                  >
                    <Euro sx={{ fontSize: 16, color: "success.main" }} />
                    <Typography variant="caption" color="success.main">
                      {formatCurrency(data.overview.potentialRevenue)} potencial
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, mb: 0.5 }}
                      >
                        {data.overview.totalPartners}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Parceiros
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: "warning.main",
                        background: (theme) =>
                          `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                      }}
                    >
                      <Group />
                    </Avatar>
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 2 }}
                  >
                    <Chip
                      label={`${data.overview.activePartners} ativos`}
                      size="small"
                      color="success"
                      sx={{ fontSize: "0.6875rem" }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Secondary Stats */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: "warning.light" }}>
                      <Schedule />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {data.overview.pendingClients}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Aguardando Verificação
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: "info.light" }}>
                      <Euro />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatCurrency(data.overview.partnerRevenue)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Receita de Parceiros
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: "success.light" }}>
                      <Assessment />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {data.overview.systemUptime}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Uptime do Sistema
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Ações Rápidas
            </Typography>
            <Grid container spacing={3}>
              {quickActions.map((action) => (
                <Grid size={{ xs: 12, md: 3 }} key={action.title}>
                  <Card
                    sx={{ height: "100%", cursor: "pointer" }}
                    onClick={() => navigate(action.path)}
                  >
                    <CardContent sx={{ p: 3, height: "100%" }}>
                      <Stack spacing={2} sx={{ height: "100%" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: `${action.color}.main`,
                              background: (theme) => {
                                const colorPalette =
                                  theme.palette[
                                    action.color as
                                      | "primary"
                                      | "secondary"
                                      | "success"
                                      | "warning"
                                  ];
                                return `linear-gradient(135deg, ${colorPalette.main}, ${colorPalette.dark})`;
                              },
                            }}
                          >
                            {action.icon}
                          </Avatar>
                          <IconButton size="small">
                            <MoreVert />
                          </IconButton>
                        </Box>

                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            {action.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {action.description}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Chip
                            label={action.stats}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.6875rem" }}
                          />
                          <Button
                            size="small"
                            sx={{ fontWeight: 600 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(action.path);
                            }}
                          >
                            Acessar
                          </Button>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Recent Activity */}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Atividade Recente
            </Typography>
            <Card>
              <CardContent sx={{ p: 0 }}>
                {recentActivity.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      Nenhuma atividade recente
                    </Typography>
                  </Box>
                ) : (
                  <Stack
                    divider={
                      <Box sx={{ borderBottom: 1, borderColor: "divider" }} />
                    }
                  >
                    {recentActivity.map((activity, index) => (
                      <Box key={index} sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: activity.type === 'client' ? "primary.light" : "secondary.light",
                            }}
                          >
                            {activity.type === 'client' ? <People /> : <Business />}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {activity.action}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {activity.user} • {activity.time}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
